import { useState } from "react";
import { memberTypes } from "@/mocks/membershipData";
import { useScrollReveal, useStaggerReveal } from "@/hooks/useScrollReveal";
import Icon from "@/components/feature/Icon";
import { formatFee, useSiteSettings, type MembershipFees } from "@/lib/siteSettings";

export default function MembershipTypes() {
  const [hovered, setHovered] = useState<string | null>(null);
  const { ref: headerRef, style: headerStyle } = useScrollReveal();
  const { containerRef, getItemStyle } = useStaggerReveal(memberTypes.length, 150);
  const { settings } = useSiteSettings();
  const fees = settings.membership.fees;
  const applicationsOpen = settings.membership.applications_open;

  return (
    <section className="pt-16 pb-24 md:pt-20 md:pb-32 bg-white">
      <div className="px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div ref={headerRef} style={headerStyle} className="max-w-3xl mb-16 md:mb-20">
          <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
            Membership Plans
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950 leading-tight mb-5">
            會員類型與會費
          </h2>
          <p className="text-silver-500 text-base md:text-lg leading-relaxed">
            我們提供三種會員資格，滿足個人專業人士、學術團體與企業的不同需求。所有會員均享有協會核心資源與活動參與權益。
          </p>
        </div>

        {/* Cards */}
        <div ref={containerRef} className="grid md:grid-cols-3 gap-6 md:gap-8">
          {memberTypes.map((plan, index) => (
            <div
              key={plan.id}
              style={getItemStyle(index)}
              className={`relative rounded-3xl border transition-all duration-500 overflow-hidden ${
                plan.highlight
                  ? "bg-navy-950 text-white border-navy-800 shadow-2xl md:-translate-y-2"
                  : "bg-white border-silver-200 hover:border-navy-300 hover:-translate-y-1 hover:shadow-xl"
              } ${hovered === plan.id && !plan.highlight ? "shadow-xl" : ""}`}
              onMouseEnter={() => setHovered(plan.id)}
              onMouseLeave={() => setHovered(null)}
            >
              {plan.highlight && (
                <div className="absolute top-5 right-5 text-xs font-medium bg-white/10 backdrop-blur-sm text-white px-3 py-1 rounded-full">
                  最受歡迎
                </div>
              )}

              <div className="p-7 md:p-8">
                <div
                  className={`inline-block text-xs font-medium text-white px-3 py-1 rounded-full mb-5 ${plan.badgeColor}`}
                >
                  {plan.subtitle}
                </div>

                <h3
                  className={`text-2xl font-semibold mb-3 ${
                    plan.highlight ? "text-white" : "text-navy-950"
                  }`}
                >
                  {plan.title}
                </h3>
                <p
                  className={`text-sm leading-relaxed mb-6 ${
                    plan.highlight ? "text-silver-300" : "text-silver-500"
                  }`}
                >
                  {plan.description}
                </p>

                <div className="mb-7">
                  <span
                    className={`text-4xl font-semibold font-serif ${
                      plan.highlight ? "text-white" : "text-navy-950"
                    }`}
                  >
                    NT$ {formatFee(fees[plan.id as keyof MembershipFees] ?? Number(plan.annualFee.replace(/,/g, "")))}
                  </span>
                  <span
                    className={`text-sm ml-2 ${
                      plan.highlight ? "text-silver-400" : "text-silver-500"
                    }`}
                  >
                    / 年
                  </span>
                </div>

                <div className={`space-y-3 ${applicationsOpen ? "mb-8" : ""}`}>
                  {plan.features.map((f) => (
                    <div key={f.label} className="flex items-center justify-between text-sm">
                      <span
                        className={
                          plan.highlight ? "text-silver-300" : "text-silver-500"
                        }
                      >
                        {f.label}
                      </span>
                      <span
                        className={`font-medium ${
                          plan.highlight ? "text-white" : "text-navy-950"
                        }`}
                      >
                        {f.value}
                      </span>
                    </div>
                  ))}
                </div>

                {applicationsOpen && (
                  <a
                    href="#join"
                    className={`inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      plan.highlight
                        ? "bg-white text-navy-950 hover:bg-silver-100"
                        : "bg-navy-950 text-white hover:bg-navy-800"
                    }`}
                  >
                    選擇此方案
                    <Icon className="ri-arrow-right-line" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-silver-500 text-sm mt-10">
          會費均以年為單位計算，入會後可依章程規定續會或升級會員類型。
          {!applicationsOpen && (
            <>
              {" "}
              會員採邀請制，
              <a href="#join" className="text-navy-800 underline underline-offset-2 hover:text-navy-950">
                入會方式請見下方說明
              </a>
              。
            </>
          )}
        </p>
      </div>
    </section>
  );
}